import type React from 'react';
import { useEffect, useState } from 'react';
import { adminApi } from '@/admin/adminApi';
import type { Brand } from '@/types/domain';

export function AdminBrandsPage() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [queueOnly, setQueueOnly] = useState(false);

  async function reload() {
    setLoading(true);
    const res = await adminApi.listBrands();
    if (res.success) setBrands(res.data);
    setLoading(false);
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    await adminApi.createBrand({ name: newName.trim(), sourceUrl: newSourceUrl.trim() || undefined });
    setNewName('');
    setNewSourceUrl('');
    setCreating(false);
    reload();
  }

  async function toggleStatus(brand: Brand) {
    const nextStatus = brand.status === 'active' ? 'locked' : 'active';
    await adminApi.updateBrand(brand.id, { status: nextStatus });
    reload();
  }

  async function toggleVerification(brand: Brand) {
    const nextStatus = brand.verificationStatus === 'verified' ? 'pending' : 'verified';
    await adminApi.updateBrand(brand.id, { verificationStatus: nextStatus });
    reload();
  }

  const researchQueue = brands.filter((b) => b.status === 'locked' || b.verificationStatus === 'pending');
  const visible = queueOnly ? researchQueue : brands;

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">Brands</h2>
        <p className="mt-1 text-sm text-muted">
          New brands start locked. A brand only becomes selectable for a given audience/category once
          it has a verified chart with real rows — see the Charts tab.
        </p>
      </div>

      <button
        type="button"
        onClick={() => setQueueOnly((v) => !v)}
        className={`min-h-[36px] rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
          queueOnly ? 'bg-brand text-ink' : 'bg-stone-100 text-muted'
        }`}
      >
        Research queue ({researchQueue.length}){queueOnly ? ' — showing only' : ''}
      </button>

      <div className="flex flex-wrap gap-2 rounded-2xl border border-stone-200 bg-white p-4">
        <input
          value={newName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
          placeholder="New brand name"
          className="min-h-[44px] flex-1 rounded-xl border border-stone-200 px-3 py-2 text-sm"
        />
        <input
          value={newSourceUrl}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSourceUrl(e.target.value)}
          placeholder="Official source URL (optional)"
          className="min-h-[44px] flex-[2] rounded-xl border border-stone-200 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating || !newName.trim()}
          className="min-h-[44px] rounded-xl bg-brand px-4 text-sm font-semibold text-ink disabled:opacity-40"
        >
          Add brand
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-stone-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 text-xs uppercase text-muted">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Slug</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Verification</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {visible.map((b) => (
                <tr key={b.id}>
                  <td className="px-3 py-2 font-medium text-ink">{b.name}</td>
                  <td className="px-3 py-2 font-mono text-muted">{b.slug}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleStatus(b)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        b.status === 'active' ? 'bg-brand/10 text-brand' : 'bg-stone-100 text-muted'
                      }`}
                    >
                      {b.status}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleVerification(b)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        b.verificationStatus === 'verified'
                          ? 'bg-brand/10 text-brand'
                          : b.verificationStatus === 'rejected'
                            ? 'bg-danger/10 text-danger'
                            : 'bg-stone-100 text-muted'
                      }`}
                    >
                      {b.verificationStatus}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {b.sourceUrl ? (
                      <a
                        href={b.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand underline underline-offset-2"
                      >
                        link
                      </a>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted">{b.notes ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
